// Enhanced notification sound generator using Web Audio API and MP3 file
export const createNotificationSound = () => {
  try {
    // Check if Web Audio API is supported
    if (!window.AudioContext && !window.webkitAudioContext) {
      console.log('Web Audio API not supported');
      return false;
    }

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Resume audio context if suspended (required by modern browsers)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Create a more pleasant notification sound
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.3);

    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.4
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.4);

    return true;
  } catch (error) {
    console.log('Could not create notification sound:', error);
    return false;
  }
};

// Enhanced beep sound with better error handling
export const playBeepSound = () => {
  try {
    // Check if Web Audio API is supported
    if (!window.AudioContext && !window.webkitAudioContext) {
      console.log('Web Audio API not supported');
      return false;
    }

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Resume audio context if suspended
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Create a pleasant beep sound
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.2);

    gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.3
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);

    return true;
  } catch (error) {
    console.log('Could not play beep sound:', error);
    return false;
  }
};

// Play order notification sound using MP3 file
export const playOrderNotificationSound = () => {
  try {
    // Resume audio context if suspended (required by modern browsers)
    if (window.AudioContext || window.webkitAudioContext) {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch((err) => {
          console.log('Could not resume audio context:', err);
        });
      }
    }

    // Create audio element for order notification
    const audio = new Audio('/vendor/notification-sound.mp3');
    audio.volume = 0.6; // Set appropriate volume

    // Reset audio to start
    audio.currentTime = 0;

    // Play the order notification sound
    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Audio is playing
          console.log('Order notification sound playing');
        })
        .catch((err) => {
          console.log(
            'Could not play order notification MP3, falling back to beep:',
            err
          );
          // Fallback to beep sound if MP3 fails
          playBeepSound();
        });
    }

    return true;
  } catch (error) {
    console.log('Could not play order notification sound:', error);
    // Fallback to beep sound
    return playBeepSound();
  }
};

// Play general notification sound (for non-order notifications)
export const playGeneralNotificationSound = () => {
  try {
    // Try to play the generated beep sound first
    const success = playBeepSound();

    // Fallback to audio file if Web Audio API fails
    if (!success) {
      const audio = new Audio('/vendor/order_notification.mp3');
      audio.volume = 0.4; // Lower volume for general notifications

      audio.play().catch((err) => {
        console.log('Could not play general notification sound:', err);
      });
    }

    return true;
  } catch (error) {
    console.log('Error playing general notification sound:', error);
    return false;
  }
};

// Test function to verify sound is working
export const testNotificationSound = () => {
  console.log('🔊 Testing notification sound...');
  const success = playBeepSound();
  if (success) {
    console.log('✅ Notification sound test successful');
  } else {
    console.log('❌ Notification sound test failed');
  }
  return success;
};

// Test function to verify order notification sound is working
export const testOrderNotificationSound = () => {
  console.log('🔊 Testing order notification sound...');
  const success = playOrderNotificationSound();
  if (success) {
    console.log('✅ Order notification sound test successful');
  } else {
    console.log('❌ Order notification sound test failed');
  }
  return success;
};
